import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { CheckCircle2, Clock, XCircle } from "lucide-react";
import api, { zar } from "@/lib/api";
import { EbikLogo } from "@/components/EbikLogo";
import { Button } from "@/components/ui/button";

export default function PaymentResult({ mode = "return" }) {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const invoiceId = params.get("invoice_id");
  const [state, setState] = useState(mode === "cancel" ? "cancelled" : "checking");
  const [info, setInfo] = useState(null);

  useEffect(() => {
    if (mode === "cancel" || !invoiceId) return;
    let tries = 0;
    let timer;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${invoiceId}`);
        setInfo(data);
        if (data.status === "Paid") { setState("paid"); return; }
      } catch { /* ignore */ }
      tries += 1;
      if (tries >= 8) { setState("pending"); return; }
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [invoiceId, mode]);

  const ICON = { checking: Clock, pending: Clock, paid: CheckCircle2, cancelled: XCircle };
  const Icon = ICON[state];
  const COLOR = state === "paid" ? "text-emerald-600" : state === "cancelled" ? "text-red-600" : "text-blue-600";
  const TITLE = {
    checking: "Confirming your payment…",
    pending: "Payment processing",
    paid: "Payment received",
    cancelled: "Payment cancelled",
  }[state];
  const MSG = {
    checking: "Please wait while we confirm your payment with PayFast.",
    pending: "We haven't received confirmation yet. Your invoice will update automatically once PayFast confirms the transaction.",
    paid: `Thank you. Invoice ${info?.invoice_number || ""} has been marked as paid.`,
    cancelled: "Your payment was cancelled. Your invoice remains outstanding — you can try again anytime.",
  }[state];

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-muted/30 p-6">
      <EbikLogo />
      <div className="mt-8 w-full max-w-md rounded-2xl border border-border bg-card p-8 text-center" data-testid="payment-result">
        <div className={`mx-auto w-16 h-16 rounded-full bg-accent flex items-center justify-center ${COLOR}`}>
          <Icon size={34} className={state === "checking" ? "animate-pulse" : ""} />
        </div>
        <h1 className="mt-5 text-2xl font-heading font-extrabold">{TITLE}</h1>
        <p className="mt-3 text-muted-foreground text-sm">{MSG}</p>
        {info && <p className="mt-2 font-mono font-semibold">{zar(info.total_zar)}</p>}
        <div className="mt-8 flex gap-3 justify-center">
          <Button className="bg-primary hover:bg-red-700" onClick={() => nav("/app/invoices")} data-testid="payment-back">Back to Invoices</Button>
          <Button variant="outline" onClick={() => nav("/app")}>Dashboard</Button>
        </div>
      </div>
    </div>
  );
}
